/**
 * Photos taken on site: the device camera (or the gallery) through a file input, reduced to a
 * JPEG of at most 1600 px so a survey with dozens of photos stays light to store and sync.
 * Works the same in browsers, the installed web app and (later) the Tauri mobile webview.
 */
import { newId } from '@elec/domain';

export const PHOTO_MAX_PX = 1600;
/**
 * Images sent to the AI (board photo, sketch). Phone photos are often 12 Mpx: a vision model's work
 * grows with the pixel count, so full size can take minutes on a self-hosted model. 2000 px keeps
 * breaker markings and handwriting readable.
 */
export const AI_IMAGE_MAX_PX = 2000;

export interface Photo {
  id: string;
  bytes: Uint8Array;
  mime: 'image/jpeg';
}

/** Ask for one or more photos; [] when cancelled. `camera`: open the camera directly on phones. */
export function pickPhotos(camera = true): Promise<File[]> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = !camera;
    if (camera) input.setAttribute('capture', 'environment');
    input.onchange = () => resolve([...(input.files ?? [])]);
    input.oncancel = () => resolve([]);
    input.click();
  });
}

/** Resize and re-encode a photo (orientation from the camera is applied by the browser). */
export async function preparePhoto(file: Blob): Promise<Photo> {
  const bitmap = await createImageBitmap(file);
  try {
    return {
      id: `photo-${newId()}.jpg`,
      bytes: await toJpeg(bitmap, PHOTO_MAX_PX, 0.82),
      mime: 'image/jpeg',
    };
  } finally {
    bitmap.close();
  }
}

/**
 * An image picked for the AI, at most AI_IMAGE_MAX_PX on its long side, as JPEG. A JPEG already
 * small enough is kept as is. If the browser cannot decode the file, it is sent unchanged.
 */
export async function prepareAiImage<M extends string>(
  bytes: Uint8Array,
  mime: M,
): Promise<{ bytes: Uint8Array; mime: M | 'image/jpeg' }> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(new Blob([bytes.slice()], { type: mime }));
  } catch {
    return { bytes, mime };
  }
  try {
    if (mime === 'image/jpeg' && Math.max(bitmap.width, bitmap.height) <= AI_IMAGE_MAX_PX)
      return { bytes, mime };
    return { bytes: await toJpeg(bitmap, AI_IMAGE_MAX_PX, 0.88), mime: 'image/jpeg' };
  } finally {
    bitmap.close();
  }
}

/**
 * A horizontal band of an AI image (fractions of its height), as JPEG: one rail of a board photo,
 * for a second, close-up reading. The band keeps the image's width.
 */
export async function cropAiImage(
  bytes: Uint8Array,
  mime: string,
  top: number,
  bottom: number,
): Promise<{ bytes: Uint8Array; mime: 'image/jpeg' }> {
  const bitmap = await createImageBitmap(new Blob([bytes.slice()], { type: mime }));
  try {
    const y = Math.round(top * bitmap.height);
    const h = Math.max(1, Math.round((bottom - top) * bitmap.height));
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = h;
    const g = canvas.getContext('2d')!;
    g.fillStyle = '#fff';
    g.fillRect(0, 0, canvas.width, h);
    g.drawImage(bitmap, 0, y, bitmap.width, h, 0, 0, bitmap.width, h);
    const blob = await new Promise<Blob>((res, rej) =>
      canvas.toBlob((b) => (b ? res(b) : rej(new Error('encode'))), 'image/jpeg', 0.9),
    );
    return { bytes: new Uint8Array(await blob.arrayBuffer()), mime: 'image/jpeg' };
  } finally {
    bitmap.close();
  }
}

async function toJpeg(bitmap: ImageBitmap, maxPx: number, quality: number): Promise<Uint8Array> {
  const scale = Math.min(1, maxPx / Math.max(bitmap.width, bitmap.height));
  const w = Math.max(1, Math.round(bitmap.width * scale));
  const h = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const g = canvas.getContext('2d')!;
  // A transparent PNG would turn black in JPEG.
  g.fillStyle = '#fff';
  g.fillRect(0, 0, w, h);
  g.drawImage(bitmap, 0, 0, w, h);
  const blob = await new Promise<Blob>((res, rej) =>
    canvas.toBlob((b) => (b ? res(b) : rej(new Error('encode'))), 'image/jpeg', quality),
  );
  return new Uint8Array(await blob.arrayBuffer());
}

/** Ask for the pages of an inspection report: photos of the paper, or the PDF; [] when cancelled. */
export function pickReportFiles(): Promise<File[]> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*,application/pdf,.pdf';
    input.multiple = true;
    input.onchange = () => resolve([...(input.files ?? [])]);
    input.oncancel = () => resolve([]);
    input.click();
  });
}

/**
 * Report pages as JPEG, at most `max`: each photo once, each PDF page rendered at about 150 dpi
 * (AI_IMAGE_MAX_PX on the long side), so the AI reads a scan and a photographed page alike.
 */
export async function prepareReportPages(files: readonly File[], max: number): Promise<Photo[]> {
  const out: Photo[] = [];
  for (const f of files) {
    if (out.length >= max) break;
    const pdf = f.type === 'application/pdf' || /\.pdf$/i.test(f.name);
    if (!pdf) {
      const bitmap = await createImageBitmap(f);
      try {
        out.push({
          id: `report-${newId()}.jpg`,
          bytes: await toJpeg(bitmap, AI_IMAGE_MAX_PX, 0.85),
          mime: 'image/jpeg',
        });
      } finally {
        bitmap.close();
      }
      continue;
    }
    const pdfjs = await import('pdfjs-dist');
    const worker = await import('pdfjs-dist/build/pdf.worker.min.mjs?url');
    pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
    const task = pdfjs.getDocument({ data: new Uint8Array(await f.arrayBuffer()) });
    const doc = await task.promise;
    try {
      for (let n = 1; n <= doc.numPages && out.length < max; n++) {
        const page = await doc.getPage(n);
        const base = page.getViewport({ scale: 1 });
        const vp = page.getViewport({
          scale: Math.min(150 / 72, AI_IMAGE_MAX_PX / Math.max(base.width, base.height)),
        });
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(vp.width);
        canvas.height = Math.round(vp.height);
        const ctx = canvas.getContext('2d')!;
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        await page.render({ canvas, canvasContext: ctx, viewport: vp }).promise;
        const bitmap = await createImageBitmap(canvas);
        try {
          out.push({
            id: `report-${newId()}.jpg`,
            bytes: await toJpeg(bitmap, AI_IMAGE_MAX_PX, 0.85),
            mime: 'image/jpeg',
          });
        } finally {
          bitmap.close();
        }
      }
    } finally {
      void task.destroy();
    }
  }
  return out;
}
